import { useEffect } from 'react';
import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { useLogout, useUser } from 'features/auth';
import { useLogout as useLogoutFederated } from 'features/auth/hooks/federatedAuthHooks';
import { useLoggedInUser } from 'features/accessControl/hooks/useLoggedInUser';
import { UserAvatar } from './UserAvatar';
import { Copy } from '@phosphor-icons/react';

import { t } from '@lingui/macro';
import { useTheme } from '@emotion/react';
import { useNotification } from 'features/notifications';

import { useDialogs } from './useDialogs';
import { USE_FEDERATED_AUTH } from 'config';
import { useCompanySwitcherDialog } from 'features/auth/hooks/useCompanySwitcherDialog';
import { Tooltip } from 'components/tooltip/Tooltip';
import { IconButton } from 'components/button/IconButton';

type UserDetailsProps = {
  /**
   * Feature-owned actions rendered next to the invite code (the send-invite icon and its
   * wizard). Injected from the route layer because shared components may not import features.
   */
  inviteActions?: ReactNode;
  isBrandSupplierConnections?: boolean;
  isFeatureFlagLoading?: boolean;
};

export const UserDetails = ({
  inviteActions,
  isBrandSupplierConnections = false,
  isFeatureFlagLoading = false,
}: UserDetailsProps) => {
  const { data: user } = useUser();
  const inviteCode = user?.loginContext?.inviteCode;

  const theme = useTheme();
  const { notify } = useNotification();
  const logout = useLogout({
    onSuccess: () => {
      window.location.replace('/');
    },
  });
  const logoutFederated = useLogoutFederated();
  const { inviteCodeDialog } = useDialogs();
  const { avatarColor } = useLoggedInUser();
  // Own the failure toast here (this component already holds the notification API); passing it
  // down keeps the switcher hooks free of a notifications import that would create an arch cycle.
  const { openCompanySwitcherDialog, hasMultiple } = useCompanySwitcherDialog(() =>
    notify.error({
      id: 'switch-company-error',
      message: t`Something went wrong while switching company. Please try again.`,
    })
  );
  const handleCopyInviteCode = async () => {
    if (inviteCode && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(inviteCode);
      notify.info({
        message: t`Copied to clipboard!`,
        id: 'invite-code-copy',
        noDuplicate: true,
      });
    }
  };

  useEffect(() => {
    // Wait for flags — while loading the flag reads as false and would flash the old dialog.
    if (isFeatureFlagLoading || isBrandSupplierConnections || !inviteCode) return;
    inviteCodeDialog(inviteCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inviteCodeDialog is recreated each render; only react when inputs change
  }, [inviteCode, isBrandSupplierConnections, isFeatureFlagLoading]);

  return (
    <Container>
      {inviteCode && (
        <InviteCodeContainer>
          <InviteCodeText>{inviteCode}</InviteCodeText>
          <Tooltip title={t`Copy invite code`}>
            <div>
              <IconButton data-testid="copy-invite" size="lg" onClick={handleCopyInviteCode}>
                <Copy size={18} color={theme.palette.border.accentPrimary} />
              </IconButton>
            </div>
          </Tooltip>
          {inviteActions}
        </InviteCodeContainer>
      )}
      <Greeting>
        <span style={{ whiteSpace: 'nowrap' }}>Good to see you, {user?.loginContext?.name}</span>
        <span role="img" aria-label="partying">
          &#129395;
        </span>
      </Greeting>
      <UserAvatar
        onLogout={USE_FEDERATED_AUTH ? logoutFederated : logout}
        letters={user?.loginContext?.name}
        color={avatarColor}
        showSwitchCompany={hasMultiple}
        onSwitchCompany={openCompanySwitcherDialog}
      />
    </Container>
  );
};

const Greeting = styled.span`
  color: ${p => p.theme.palette.utility.black};
  align-items: center;
  display: flex;
  gap: 0.25rem;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: center;
  span[role='img'] {
    font-size: 1.2rem;
  }
`;
const InviteCodeContainer = styled.div`
  border-radius: 4px;
  padding-left: 8px;
  padding-right: 4px;
  background-color: ${p => p.theme.palette.utility.marineBlue[100]};
  display: flex;
  align-items: center;
  white-space: nowrap;
  button {
    :hover {
      color: inherit;
      background-color: inherit;
    }
  }
`;
const InviteCodeText = styled.span`
  padding: 4px 0;
  font-size: 12px;
  color: ${p => p.theme.palette.text.link};
`;
